'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useEffect, useRef } from 'react';

export function About({
  title,
  image,
  description,
}: {
  title: string;
  image: string;
  description: string;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const words = description.split(/(\s+)/);

  useEffect(() => {
    const section = sectionRef.current;
    const text = textRef.current;
    if (!section || !text) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const spans = Array.from(text.querySelectorAll<HTMLElement>('[data-word]'));
    let frame = 0;
    let visible = false;
    const update = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const p = (window.innerHeight - rect.top) / Math.max(1, rect.height);
      const stretched = Math.max(0, Math.min(1, (p - 0.25) / 1.1));
      const progress = motion.matches
        ? 1
        : Math.pow(stretched * stretched * (3 - 2 * stretched), 1.25);
      spans.forEach((span, index) => {
        span.style.opacity = String(
          0.4 + 0.6 * Math.max(0, Math.min(1, progress * spans.length - index)),
        );
      });
    };
    const schedule = () => {
      if (visible && !frame) frame = requestAnimationFrame(update);
    };
    const observe = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      if (visible) schedule();
    });
    observe.observe(section);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    motion.addEventListener('change', schedule);
    return () => {
      observe.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      motion.removeEventListener('change', schedule);
    };
  }, [description]);

  return (
    <section
      id="about"
      ref={sectionRef}
      className={cn(
        'custom-section',
        'relative py-6 sm:py-10 md:py-12 lg:py-24',
        'ea-about__section',
      )}
    >
      <div className="custom-container flex flex-col items-start justify-start gap-6 sm:gap-8 md:flex-row md:items-stretch md:justify-between lg:gap-12">
        <div className="flex w-full min-w-0 flex-col items-start justify-start gap-12 md:max-w-[768px] md:flex-[0_0_768px]">
          <h2 className="text-sm font-semibold text-white">{title}</h2>
          <p
            ref={textRef}
            className="h3 w-full whitespace-pre-wrap break-words font-semibold uppercase text-white"
          >
            {words.map((word, index) =>
              /\S/.test(word) ? (
                <span
                  key={index}
                  data-word
                  className="transition-opacity duration-150 motion-reduce:!opacity-100"
                  style={{ opacity: 0.4 }}
                >
                  {word}
                </span>
              ) : (
                word
              ),
            )}
          </p>
        </div>
        <div className="relative h-[440px] w-full overflow-hidden sm:h-[500px] md:h-auto md:min-h-[450px] md:max-w-[384px] md:flex-1">
          <Image
            src={image}
            alt={title}
            fill
            sizes="(max-width: 768px) 100vw, 384px"
            className="rounded-[4px] object-cover object-center"
          />
        </div>
      </div>
    </section>
  );
}
